import { useEffect, useState, useMemo } from "react";
import { GasOrder } from "../../types/gasOrder.types";
import { getPendingPayments, processManualPayment } from "../../services/pendingPaymentsService";
import { getStations } from "../../services/stationsService";
import { Station } from "../../types/stations.types";
import { FormControl, InputLabel, MenuItem, Select, IconButton, Tooltip } from "@mui/material";
import { Sync as SyncIcon } from "@mui/icons-material";
import {
  PageContainer,
  PageHeader,
  PageTitle,
} from "../../components/PageLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { toast } from "react-toastify";
import { formatIsoToBrDateTimeShort } from "../../utils/functions";

export default function PendingPayments() {
  const [selectedStationId, setSelectedStationId] = useState<string>("");
  const [selectedGasPump, setSelectedGasPump] = useState<string>("all");
  const [stations, setStations] = useState<Station[]>([]);
  const [allOrders, setAllOrders] = useState<GasOrder[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchStations() {
      try {
        const data = await getStations();
        setStations(data);
        if (data.length > 0) {
          setSelectedStationId(data[0].id);
        }
      } catch {
        toast.error("Erro ao carregar postos.");
      }
    }
    fetchStations();
  }, []);

  const loadOrders = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await getPendingPayments();
      setAllOrders(data);
    } catch {
      if (!silent) toast.error("Erro ao carregar pagamentos pendentes.");
      setAllOrders((prev) => (silent ? prev : []));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    const intervalId = setInterval(() => loadOrders(true), 30 * 1000);
    return () => clearInterval(intervalId);
  }, []); // fetches all regardless of selected station

  useEffect(() => {
    setSelectedGasPump("all");
  }, [selectedStationId]);

  const availablePumps = useMemo(() => {
    const pumps = allOrders
      .filter((o) => o.station?.id === selectedStationId)
      .map((o) => o.gasPump);
    return Array.from(new Set(pumps)).sort((a, b) => Number(a) - Number(b));
  }, [allOrders, selectedStationId]);

  const orders = useMemo(() => {
    let filtered = allOrders.filter(
      (order) => order.station?.id === selectedStationId || order.station?.id === "MOCK_STATION"
    );
    if (selectedGasPump && selectedGasPump !== "all") {
      filtered = filtered.filter((order) => order.gasPump === selectedGasPump);
    }
    return filtered;
  }, [allOrders, selectedStationId, selectedGasPump]);

  const handleManualPayment = async (id: string) => {
    try {
      const responseMessage = await processManualPayment(id);
      toast.success(typeof responseMessage === 'string' ? responseMessage : "Pagamento processado com sucesso");
      loadOrders(true); // Recarrega tela
    } catch (e) {
      toast.error("Erro ao processar pagamento");
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Pagamentos pendentes (Combustível)</PageTitle>
        <div style={{ display: 'flex', gap: '16px' }}>
          <FormControl size="small" sx={{ minWidth: 250, backgroundColor: "#fff" }}>
            <InputLabel id="station-select-label">Posto</InputLabel>
            <Select
              labelId="station-select-label"
              value={selectedStationId}
              label="Posto"
              onChange={(e) => setSelectedStationId(e.target.value)}
            >
              {stations.length === 0 ? (
                <MenuItem disabled value="">
                  Nenhum posto disponível
                </MenuItem>
              ) : (
                stations.map((station) => (
                  <MenuItem key={station.id} value={station.id}>
                    {station.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedStationId && availablePumps.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150, backgroundColor: "#fff" }}>
              <InputLabel id="pump-select-label">Bomba</InputLabel>
              <Select
                labelId="pump-select-label"
                value={selectedGasPump}
                label="Bomba"
                onChange={(e) => setSelectedGasPump(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                {availablePumps.map((pump) => (
                  <MenuItem key={pump} value={pump}>
                    Bomba {pump}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </PageHeader>

      {!selectedStationId ? (
        <p>Selecione um posto para visualizar os pagamentos pendentes.</p>
      ) : loading && allOrders.length === 0 ? (
        <p>Carregando...</p>
      ) : orders.length === 0 ? (
        <p>Nenhum pagamento pendente encontrado para este posto.</p>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: "auto", marginTop: "16px" }}>
          <Table sx={{ minWidth: 650 }} aria-label="pending payments table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Bomba / Tipo</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Cliente</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Data da transação</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Quantidade</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Valor</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                    Bomba {order.gasPump} · {order.type}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {order.user?.full_name || order.user?.email || "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {order.updatedAt
                      ? formatIsoToBrDateTimeShort(order.updatedAt)
                      : "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} align="right">
                    {order.quantity.toFixed(2)} L
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} align="right">
                    R$ {order.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} align="center">
                    <Tooltip title="Processar pagamento manual PIX">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleManualPayment(order.id)}
                      >
                        <SyncIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
}
